import {
  TableState,
  ColumnVisibility,
  ColumnsType,
  PlotLayoutItem,
} from "@/types/tableState.types";
import { SortItem } from "@/components/UI/TableToolbar/Sorting/types";
import {
  LegacyWorkspaceConfig,
  LegacyConfigFields,
  WorkspaceConfig,
  Tab,
  DashboardTab,
  TableTab,
  WorkspaceFilterValue,
} from "@/types/workspaceConfig";
import {
  sanitizePlotRuntimeFields,
  sanitizePlotsRuntimeFields,
  normalizeWorkspaceConfig,
} from "@/utils/workspaceConfig";

// Collapse duplicate columns by fieldName (keep the first occurrence). Two
// column entries that share a fieldName render with the same React key and are
// toggled together by the column selector's fieldName-keyed visibility handler,
// so a duplicated field appears twice and cannot be hidden individually. The
// duplicate can originate in backend metadata or a saved workspace config, so
// every path that builds the column list guards against it here.
export function dedupeColumnsByField(columns: ColumnsType[]): ColumnsType[] {
  const seen = new Set<string>();
  return columns.filter((column) => {
    if (seen.has(column.fieldName)) return false;
    seen.add(column.fieldName);
    return true;
  });
}

export function createDefaultTableState(
  metadataColumns?: string[],
  defaultHiddenColumns?: string[],
): TableState {
  const hiddenSet = new Set(defaultHiddenColumns ?? []);
  return {
    filters: {},
    columns: metadataColumns
      ? dedupeColumnsByField(
          metadataColumns.map((col) => ({
            fieldName: col,
            visibility: hiddenSet.has(col)
              ? ColumnVisibility.HIDDEN
              : ColumnVisibility.VISIBLE,
            mandatory: false,
          })),
        )
      : [],
    lockedColumns: 0,
    sorting: [],
    pagination: { current: 1, pageSize: 100, total: 0 },
    plots: [],
    plotLayoutsByBreakpoint: {},
    screenView: "table",
  };
}

/**
 * Persisted workspaces exist in the wild holding the METADATA column shape
 * ({field, label}) rather than the table-state shape ({fieldName, visibility}).
 * Those entries have no fieldName, so every cell bound to them rendered "-".
 * Coerce them here so an already-saved bad workspace repairs itself on load.
 */
function toColumnEntry(column: ColumnsType): ColumnsType {
  if (!column) return column;

  // fieldName itself holding a {field, label} object: written by table state that
  // was seeded with raw metadata columns.
  const nested = column.fieldName as unknown as { field?: string } | string;
  if (nested && typeof nested === "object" && nested.field) {
    return {
      ...column,
      fieldName: nested.field,
      visibility: column.visibility ?? ColumnVisibility.VISIBLE,
      mandatory: column.mandatory ?? false,
    };
  }

  // The whole entry in metadata shape, with no fieldName at all.
  if (!column.fieldName) {
    const legacyField = (column as unknown as { field?: string }).field;
    if (legacyField) {
      return {
        ...column,
        fieldName: legacyField,
        visibility: column.visibility ?? ColumnVisibility.VISIBLE,
        mandatory: column.mandatory ?? false,
      };
    }
  }

  return column;
}

export function normalizeColumns(
  columns: ColumnsType[] | undefined,
  metadataColumns?: string[],
) {
  if (!columns) return [];
  const coerced = columns.map(toColumnEntry).filter((c) => c.fieldName);
  if (!metadataColumns) return dedupeColumnsByField(coerced);
  const allowed = new Set(metadataColumns);
  const kept = dedupeColumnsByField(
    coerced.filter((column) => allowed.has(column.fieldName)),
  );
  // Append dataset columns added after this state was persisted so new
  // backend fields show up instead of being silently dropped.
  const present = new Set(kept.map((column) => column.fieldName));
  const added: ColumnsType[] = [];
  for (const col of metadataColumns) {
    if (present.has(col)) continue;
    present.add(col);
    added.push({
      fieldName: col,
      visibility: ColumnVisibility.VISIBLE,
      mandatory: false,
    });
  }
  return [...kept, ...added];
}

export function normalizeFilters(
  filters: Record<string, WorkspaceFilterValue> | undefined,
  metadataFilterKeys?: string[],
  metadataColumns?: string[],
) {
  if (!filters) return {};
  const allowedKeys = metadataFilterKeys ?? metadataColumns;
  if (!allowedKeys) return filters;
  const allowed = new Set(allowedKeys);
  return Object.fromEntries(
    Object.entries(filters).filter(([key]) => allowed.has(key)),
  );
}

export function normalizeSorting(
  sorting: SortItem[] | undefined,
  metadataColumns?: string[],
) {
  if (!sorting) return [];
  if (!metadataColumns) return sorting;
  const allowed = new Set(metadataColumns);
  return sorting.filter((item) => allowed.has(item.field));
}

// Seeds per-axis flags from legacy showTicks/showAxesLabels on load; saving
// the workspace back persists the migrated, legacy-free shape.
// TODO: delete this, migrateLegacyPlots, and LegacyConfigFields
// once no persisted configs carry the legacy keys.
export function migrateLegacyPlotConfig(config: any & LegacyConfigFields) {
  const { showTicks, showAxesLabels, ...rest } = config || {};
  return {
    ...rest,
    showXTicks: config?.showXTicks ?? showTicks ?? true,
    showYTicks: config?.showYTicks ?? showTicks ?? true,
    showXAxisLabel: config?.showXAxisLabel ?? showAxesLabels ?? true,
    showYAxisLabel: config?.showYAxisLabel ?? showAxesLabels ?? true,
  };
}

export function migrateLegacyPlots(plots?: any[]) {
  return (plots || []).map((plot) => ({
    ...plot,
    config: migrateLegacyPlotConfig(plot.config),
  }));
}

// Reduce a column to the fields a user can actually change, so a stray runtime
// property — or a saved config that omits `mandatory` entirely where hydration
// writes `mandatory: false` — cannot read as an edit. lodash isEqual counts own
// keys, so {fieldName, visibility} and {fieldName, visibility, mandatory: false}
// compare unequal without this projection.
function toComparableColumn(column: ColumnsType) {
  return {
    fieldName: column.fieldName,
    visibility: column.visibility,
    mandatory: column.mandatory ?? false,
  };
}

export function createComparableWorkspaceState(
  state: TableState,
  options?: {
    excludeSubjectFilter?: boolean;
    metadataColumns?: string[];
    metadataFilterKeys?: string[];
  },
) {
  const filters = { ...(state?.filters ?? {}) };
  const columns = state?.columns ?? [];
  const sorting = state?.sorting ?? [];

  if (options?.excludeSubjectFilter) {
    delete filters.subject;
  }

  const metadataColumns = options?.metadataColumns;
  const comparableColumns = metadataColumns
    ? columns.filter((column) => metadataColumns.includes(column.fieldName))
    : columns;

  const comparableFilters = metadataColumns
    ? Object.fromEntries(
        Object.entries(filters).filter(([key]) =>
          metadataColumns.includes(key),
        ),
      )
    : filters;

  const comparableSorting = metadataColumns
    ? sorting.filter((item) => metadataColumns.includes(item.field))
    : sorting;

  return structuredClone({
    filters: comparableFilters,
    columns: comparableColumns.map(toComparableColumn),
    sorting: comparableSorting,
    lockedColumns: state?.lockedColumns ?? 0,
    // Pass the metadata through so plot filters are pruned to the same key set
    // on both sides. Hydration already prunes the live state's plotFilters via
    // sanitizePlotsRuntimeFields(plots, metadataColumns, metadataFilterKeys);
    // without the same arguments here the saved side keeps a stale plot filter
    // key the live side dropped, and the two can never compare equal.
    plots: sanitizePlotsRuntimeFields(
      state?.plots,
      options?.metadataColumns,
      options?.metadataFilterKeys,
    ),
    plotLayoutsByBreakpoint: state?.plotLayoutsByBreakpoint ?? {},
  });
}

/**
 * Strips a table state's plot layouts down to the fields that matter for an
 * equality comparison (drift/unsaved-changes detection), dropping runtime
 * flags like `static`/`isDraggable`/`isResizable` that change with edit mode
 * but don't represent a real layout difference.
 */
export function comparableLayouts(state?: TableState) {
  return Object.fromEntries(
    Object.entries(state?.plotLayoutsByBreakpoint || {}).map(
      ([breakpoint, items]) => [
        breakpoint,
        (items || []).map(({ i, x, y, w, h }: PlotLayoutItem) => ({
          i,
          x,
          y,
          w,
          h,
        })),
      ],
    ),
  );
}

export function workspaceConfigToTableState(
  config: WorkspaceConfig | LegacyWorkspaceConfig | unknown,
  metadataColumns?: string[],
  metadataFilterKeys?: string[],
): TableState {
  const wc = normalizeWorkspaceConfig(config);

  const tableTab = wc.tabs?.find((t): t is TableTab => t.type === "table");
  const dashboardTab = wc.tabs?.find(
    (t): t is DashboardTab => t.type === "dashboard",
  );

  const rawColumns =
    tableTab?.config.columns ||
    metadataColumns?.map((col) => ({
      fieldName: col,
      visibility: ColumnVisibility.VISIBLE,
      mandatory: false,
    })) ||
    [];

  // Same normalization as persisted-state hydration (normalizeColumns):
  // drop columns the dataset no longer serves AND append dataset columns
  // added after the workspace was saved. Without the append, a workspace
  // saved before a new backend column existed can never compare equal to
  // the live state, so the "unsaved changes" banner shows on every load.
  const columns = normalizeColumns(rawColumns, metadataColumns);

  const rawFilters = wc.global?.filters || {};
  const allowedFilterKeys = metadataFilterKeys ?? metadataColumns;
  const filters = allowedFilterKeys
    ? Object.fromEntries(
        Object.entries(rawFilters).filter(([key]) =>
          allowedFilterKeys.includes(key),
        ),
      )
    : rawFilters;

  const rawSorting = tableTab?.config.sorting || [];
  const sorting = metadataColumns
    ? rawSorting.filter((s) => metadataColumns.includes(s.field))
    : rawSorting;

  const rawPagination = tableTab?.config.pagination;
  const dashboardItems = dashboardTab?.config || [];
  // Same sanitization as persisted-state hydration: strip runtime-only fields
  // and prune plot filters to keys the dataset still serves. A saved workspace
  // that predates a filter's removal otherwise keeps that key forever, so the
  // live state can never match it and the unsaved-changes banner never clears.
  const plots = sanitizePlotsRuntimeFields(
    migrateLegacyPlots(dashboardItems.map((item) => item.config)),
    metadataColumns,
    metadataFilterKeys,
  );
  const plotLayoutsByBreakpoint = dashboardItems.reduce(
    (layouts, item) => {
      for (const [breakpoint, layout] of Object.entries(item.layout || {})) {
        if (!layout) continue;
        const key = breakpoint as keyof typeof layouts;
        layouts[key] = [...(layouts[key] || []), layout];
      }
      return layouts;
    },
    {} as NonNullable<TableState["plotLayoutsByBreakpoint"]>,
  );

  return {
    filters: filters as TableState["filters"],
    columns,
    lockedColumns: tableTab?.config.lockedColumns || 0,
    sorting,
    pagination: {
      current: 1,
      pageSize: rawPagination?.pageSize || 100,
      total: 0,
    },
    plots,
    plotLayoutsByBreakpoint,
    screenView: wc.screenView || "table",
  };
}

export function tableStateToWorkspaceConfig(
  state: TableState,
  excludeFilterKeys?: string[],
): WorkspaceConfig {
  const filters = excludeFilterKeys?.length
    ? Object.fromEntries(
        Object.entries(state.filters || {}).filter(
          ([key]) => !excludeFilterKeys.includes(key),
        ),
      )
    : state.filters || {};

  const tabs: Tab[] = [
    {
      name: "Table",
      type: "table",
      config: {
        columns: state.columns || [],
        lockedColumns: state.lockedColumns || 0,
        sorting: state.sorting || [],
        pagination: {
          current: 1,
          pageSize: state.pagination?.pageSize || 100,
        },
      },
    },
  ];

  const layoutsByPlotId = new Map<string, Record<string, PlotLayoutItem>>();
  for (const [breakpoint, items] of Object.entries(
    state.plotLayoutsByBreakpoint || {},
  )) {
    for (const item of items || []) {
      const entry = layoutsByPlotId.get(item.i) || {};
      entry[breakpoint] = item;
      layoutsByPlotId.set(item.i, entry);
    }
  }

  tabs.push({
    name: "Dashboard",
    type: "dashboard",
    config: state.plots?.length
      ? state.plots.map((plot) => {
          const plotId = plot?.config?.plotId;
          const layout = plotId ? layoutsByPlotId.get(plotId) : undefined;

          return {
            type: "plot" as const,
            config: sanitizePlotRuntimeFields(plot),
            layout: layout && Object.keys(layout).length ? layout : undefined,
          };
        })
      : [],
  });

  return {
    global: { filters },
    tabs,
    screenView: state.screenView,
    version: "2.0.0",
  };
}
